import React, { useState, useEffect } from 'react';
import {
	Wrapper,
	Main,
	Title,
	ChartWrapper,
	LoaderWrapper,
	AnalysisWrapper,
	TableContainer,
	TableHead,
	TableRow,
	TableCellGroup,
	TableCellPeerCount,
	TableCellMin,
	TableCellChart,
	TableCellMax,
	TableBody,
	DateRangeWrapper,
	MetadataSummaryLine,
	SummaryLabel,
	SummaryValue,
} from './BenchmarkContainer.styles';
import { SecondaryNav } from 'Benchmarks/SecondaryNav';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import { ChartOptions } from 'Benchmarks/ChartOptions';
import CategoryHierarchy from '../CategoryHierarchy/CategoryHierarchy';
import {
	exportBenchmarkData,
	getCategoryInsights,
	getRevenueYear,
	saveRevenueYear,
} from 'utils/apiRequests';
import ChartLegend from '../ChartLegend/ChartLegend';
import { UNIT_MAP } from 'utils/number';
import EditRevenue from './EditRevenue/EditRevenue';
import DownloadProgressbar from 'Data/DownloadData/DownloadProgressBar';
import { NoDataFilters } from 'Benchmarks/NoData/NoDataFilter';
import Modal from 'components/Modal';
import MonthPicker from 'Settings/FirstUsageSettings/MonthPicker';
import { getDateFormat } from 'Benchmarks/utils';

function findAndModifyData(
	arr,
	targetId,
	targetLevel,
	targetGroup,
	newChildren
) {
	for (const obj of arr) {
		if (
			obj.name === targetId &&
			obj.level === targetLevel &&
			obj.group === targetGroup
		) {
			obj.children = newChildren;
			obj.isExpanded = true;
			return true;
		}
		if (obj.children && obj.children.length > 0) {
			if (
				findAndModifyData(
					obj.children,
					targetId,
					targetLevel,
					targetGroup,
					newChildren
				)
			) {
				return true;
			}
		}
	}
	return false;
}

function updateIsExpanded(data, name, level, group, isExpanded) {
	for (const item of data) {
		if (item.name === name && item.level === level && item.group === group) {
			item.isExpanded = !isExpanded;
			return true;
		}
		if (item.children && item.children.length > 0) {
			if (updateIsExpanded(item.children, name, level, group, isExpanded)) {
				return true;
			}
		}
	}
	return false;
}

const initialRevenueData = {
	revenue: '',
	revenueTo: '',
	revenueFrom: '',
};

const initialExport = {
	showProgressBar: false,
	fileDownloadFailed: false,
};

const BenchmarkContainer = props => {
	const { companies, industryOptions, initialFilters, dateRangeData } = props;
	const { dateFrom, dateTo } = getDateFormat(dateRangeData.selectedRange);
	const initialPayload = {
		flag: 0,
		revenueStart: initialFilters.minMaxSpendRange[0] * UNIT_MAP.m,
		revenueEnd: initialFilters.minMaxSpendRange[1] * UNIT_MAP.m,
		industry: [],
		dateFrom: dateFrom,
		dateTo: dateTo,
	};
	const [isChartLoading, setIsChartLoading] = useState(null);
	const [isFetchingRevenue, setIsFetchingRevenue] = useState(true);
	const [isShowRevenue, setIsShowRevenue] = useState(false);
	const [revenueData, setRevenueData] = useState(initialRevenueData);
	const [isRevenueSaving, setIsRevenueSaving] = useState(false);
	const [isFirstTimeEnterRevenue, setIsFirstTimeEnterRevenue] = useState(false);
	const [payload, setPayload] = useState(initialPayload);
	const [chartOptions, setChartOptions] = useState(initialFilters);
	const [hierarchyData, setHierarchyData] = useState([]);
	const [selectedIndustry, setSelectedIndustry] = useState([]);
	const [exportStatus, setExportStatus] = useState(initialExport);
	const [isshowSpendYearFilter, setIsShowSpendYearFilter] = useState(false);
	const [dateRange, setDateRange] = useState({
		srcRange: dateRangeData.wholeRange,
		stateRange: dateRangeData.selectedRange,
	});

	const loadChart = async value => {
		const param = !value ? payload : value;
		setIsChartLoading(true);
		await getCategoryInsights(param)
			.then(res => {
				if (res.length > 0) {
					const result = res.map(el => {
						return {
							...el,
							min:
								el.min <= 0.05
									? Number(el.min.toFixed(2))
									: Number(el.min.toFixed(1)),
							max: Number(el.max.toFixed(1)),
							median: Number(el.median.toFixed(1)),
							clientSpend: Number(el.clientSpend.toFixed(1)),
							children: [],
							isExpanded: false,
						};
					});

					const sortedResult = result.sort((a, b) => b.peerCount - a.peerCount);
					if (result[0].name !== 'Spend as % of Revenue') {
						const newData = hierarchyData;
						if (result[0].group === 2) {
							findAndModifyData(
								newData,
								param.name,
								param.flag - 3,
								param.group,
								sortedResult
							);
						} else {
							findAndModifyData(
								newData,
								param.name,
								param.flag,
								param.group,
								sortedResult
							);
						}
						setHierarchyData(newData);
						setIsChartLoading(false);
						return;
					} else {
						setHierarchyData(sortedResult);
						setIsChartLoading(false);
					}
				} else {
					setIsChartLoading(false);
				}
				return;
			})
			.catch(() => setIsChartLoading(false));
	};

	const fetchRevenueYear = async () => {
		await getRevenueYear()
			.then(response => {
				if (response.succeeded) {
					setRevenueData({
						revenue: response.data.revenue,
						revenueTo: response.data.revenueTo,
						revenueFrom: response.data.revenueFrom,
					});
					setIsFetchingRevenue(false);
					if (
						response.data.revenue === null ||
						response.data.revenueTo === null ||
						response.data.revenueFrom === null
					) {
						setIsFirstTimeEnterRevenue(true);
						setIsShowRevenue(true);
					}
				}
			})
			.catch(() => setIsFetchingRevenue(false));
	};

	const onSaveRevenueYear = async param => {
		setIsRevenueSaving(true);
		await saveRevenueYear(param)
			.then(response => {
				if (response.succeeded) {
					setIsRevenueSaving(false);
					setIsShowRevenue(false);
					setRevenueData(param);
					if (isFirstTimeEnterRevenue) {
						setIsFirstTimeEnterRevenue(false);
					}
					let newPayload = payload;
					newPayload.flag = 0;
					loadChart(newPayload);
				}
			})
			.catch(() => setIsRevenueSaving(false));
	};

	useEffect(() => {
		fetchRevenueYear();
		// eslint-disable-next-line
	}, []);

	useEffect(
		() => {
			if (revenueData.revenue !== null) {
				if (!isFetchingRevenue) {
					loadChart();
				}
			}
		}, // eslint-disable-next-line
		[isFetchingRevenue]
	);

	const handleSpendRangeChange = spendRange => {
		setChartOptions(prevState => ({
			...prevState,
			spendRange,
		}));
		setPayload(prevState => ({
			...prevState,
			flag: 0,
			revenueStart: spendRange[0] * UNIT_MAP.m,
			revenueEnd: spendRange[1] * UNIT_MAP.m,
		}));
	};

	const handleRangeLowerChange = value => {
		let number = Number(value);
		if (number > chartOptions.spendRange[1]) {
			number = chartOptions.spendRange[1];
		}
		if (number < 0) {
			number = 0;
		}
		setChartOptions(prevState => ({
			...prevState,
			spendRange: [number, chartOptions.spendRange[1]],
		}));
		setPayload(prevState => ({
			...prevState,
			flag: 0,
			revenueStart: number * UNIT_MAP.m,
		}));
	};

	const handleRangeUpperChange = value => {
		let number = Number(value);
		if (number < 0) {
			number = 0;
		}
		setChartOptions(prevState => ({
			...prevState,
			spendRange: [chartOptions.spendRange[0], number],
		}));
		setPayload(prevState => ({
			...prevState,
			flag: 0,
			revenueEnd: number * UNIT_MAP.m,
		}));
	};

	const onExpand = (categoryGroup, categoryLevel, categoryName) => {
		let newPayload = payload;
		newPayload.name = categoryName;
		newPayload.group = categoryGroup;
		if (categoryGroup === 1) {
			if (categoryLevel === 2) {
				newPayload.L1Category = categoryName;
				newPayload.flag = categoryLevel;
				setPayload(prevState => ({
					...prevState,
					...newPayload,
				}));
				loadChart(newPayload);
			} else if (categoryLevel === 3) {
				newPayload.L2Category = categoryName;
				newPayload.flag = categoryLevel;
				setPayload(prevState => ({
					...prevState,
					...newPayload,
				}));
				loadChart(newPayload);
			} else {
				newPayload.flag = categoryLevel;
				setPayload(prevState => ({
					...prevState,
					...newPayload,
				}));
				loadChart(newPayload);
			}
		} else if (categoryGroup === 2) {
			if (categoryLevel === 2) {
				newPayload.flag = 5;
				newPayload.L1Category = categoryName;
				setPayload(prevState => ({
					...prevState,
					...newPayload,
					flag: 5,
				}));
				loadChart(newPayload);
			} else if (categoryLevel === 3) {
				newPayload.flag = 6;
				newPayload.L2Category = categoryName;
				setPayload(prevState => ({
					...prevState,
					...newPayload,
					flag: 6,
				}));
				loadChart(newPayload);
			} else {
				newPayload.flag = 4;
				setPayload(prevState => ({
					...prevState,
					...newPayload,
					flag: 4,
				}));
				loadChart(newPayload);
			}
		}
	};

	const onToggle = (categoryLevel, categoryName, group, expandedFlag) => {
		const newData = hierarchyData;
		updateIsExpanded(newData, categoryName, categoryLevel, group, expandedFlag);
		setHierarchyData([...newData]);
	};

	const onChangeIndustry = e => {
		setSelectedIndustry(e);
		setPayload(prevState => ({
			...prevState,
			flag: 0,
			industry: e,
			revenueStart: prevState.revenueStart,
			revenueEnd: prevState.revenueEnd,
		}));
	};

	const handleClearAllIndustry = () => {
		setSelectedIndustry([]);
		setPayload(prevState => ({
			...prevState,
			flag: 0,
			industry: [],
		}));
	};

	const handleExport = () => {
		const bodyParam = {
			industry: payload.industry,
			revenueStart: payload.revenueStart,
			revenueEnd: payload.revenueEnd,
			dateFrom: payload.dateFrom,
			dateTo: payload.dateTo,
		};
		setExportStatus({
			showProgressBar: true,
			fileDownloadFailed: false,
		});

		exportBenchmarkData(bodyParam)
			.then(() => {
				setExportStatus(prevState => ({
					...prevState,
					fileDownloadFailed: false,
				}));
			})
			.catch(() => {
				setExportStatus(prevState => ({
					...prevState,
					fileDownloadFailed: false,
				}));
			});
	};

	const handleCloseProgressBar = () => {
		setExportStatus(prevState => ({
			...prevState,
			showProgressBar: false,
		}));
	};

	const handleApplyFilter = () => loadChart(payload);
	const handleResetFilters = () => {
		setPayload(initialPayload);
		setRevenueData(initialRevenueData);
		setChartOptions(initialFilters);
		setSelectedIndustry([]);
	};

	const handleSpendYearChange = e => {
		const minMaxDates = getDateFormat(e);
		setDateRange(prevState => ({
			...prevState,
			stateRange: e,
		}));
		setPayload(prevState => ({
			...prevState,
			...minMaxDates,
		}));
		let newPayload = payload;
		newPayload.dateFrom = minMaxDates.dateFrom;
		newPayload.dateTo = minMaxDates.dateTo;
		loadChart(newPayload);
		setIsShowSpendYearFilter(false);
	};

	const isAnyRevenueL1Expanded =
		hierarchyData.length > 0 &&
		hierarchyData[0].children.filter(x => x.isExpanded === true).length !== 0;
	const isAnySpendL1Expanded =
		hierarchyData.length > 0 &&
		hierarchyData[1].children.filter(x => x.isExpanded === true).length !== 0;

	return (
		<Wrapper>
			<SecondaryNav companies={companies.map(c => c.name)} />
			<Main>
				<ChartWrapper>
					<Title>Benchmark Analysis</Title>
					{isChartLoading === null ? (
						<></>
					) : (
						<>
							{isChartLoading ? (
								<LoaderWrapper>
									<ApLoaderDefault loaderText={() => <ApLoaderText />} />
								</LoaderWrapper>
							) : (
								<>
									<ChartOptions
										config={chartOptions}
										industryOptions={industryOptions}
										selectedIndustry={selectedIndustry}
										onChangeIndustry={onChangeIndustry}
										handleSpendRangeChange={handleSpendRangeChange}
										handleRangeLowerChange={handleRangeLowerChange}
										handleRangeUpperChange={handleRangeUpperChange}
										handleClearAllIndustry={handleClearAllIndustry}
										handleApplyFilter={handleApplyFilter}
										isShowRevenue={isShowRevenue}
										setIsShowRevenue={setIsShowRevenue}
										handleExport={handleExport}
										setIsShowSpendYearFilter={setIsShowSpendYearFilter}
									/>
									{hierarchyData.length > 0 ? (
										<>
											<AnalysisWrapper
												isExpanded={hierarchyData[0].isExpanded}
												index="0"
												isScroll={isAnyRevenueL1Expanded}
											>
												<TableContainer>
													<TableHead>
														<TableRow>
															<TableCellGroup />
															<TableCellPeerCount>
																Peer Count
															</TableCellPeerCount>
															<TableCellMin title="Lower end of interquartile range">
																Low End(%)
															</TableCellMin>
															<TableCellChart>
																<ChartLegend />
															</TableCellChart>
															<TableCellMax title="Higher end of interquartile range">
																High End(%)
															</TableCellMax>
														</TableRow>
													</TableHead>
													<TableBody>
														<CategoryHierarchy
															key={hierarchyData[0].id}
															data={[hierarchyData[0]]}
															onExpand={onExpand}
															onToggle={onToggle}
														/>
													</TableBody>
												</TableContainer>
											</AnalysisWrapper>
											<AnalysisWrapper
												isExpanded={hierarchyData[1].isExpanded}
												index="1"
												isScroll={isAnySpendL1Expanded}
											>
												<TableContainer>
													<TableHead>
														<TableRow>
															<TableCellGroup />
															<TableCellPeerCount>
																Peer Count
															</TableCellPeerCount>
															<TableCellMin title="Lower end of interquartile range">
																Low End(%)
															</TableCellMin>
															<TableCellChart>
																<ChartLegend />
															</TableCellChart>
															<TableCellMax title="Higher end of interquartile range">
																High End(%)
															</TableCellMax>
														</TableRow>
													</TableHead>
													<TableBody>
														<CategoryHierarchy
															key={hierarchyData[1].id}
															data={[hierarchyData[1]]}
															onExpand={onExpand}
															onToggle={onToggle}
														/>
													</TableBody>
												</TableContainer>
											</AnalysisWrapper>
										</>
									) : (
										<NoDataFilters reset={handleResetFilters} />
									)}
								</>
							)}
						</>
					)}
				</ChartWrapper>
				{isShowRevenue && (
					<>
						<EditRevenue
							isFirstTimeEnterRevenue={isFirstTimeEnterRevenue}
							isRevenueSaving={isRevenueSaving}
							revenueData={revenueData}
							onSaveRevenueYear={onSaveRevenueYear}
							setIsShowRevenue={setIsShowRevenue}
						/>
					</>
				)}
				{isshowSpendYearFilter && (
					<Modal
						width="620px"
						zIndex="10000000"
						cancelLabel={'Cancel'}
						onClose={() => setIsShowSpendYearFilter(false)}
					>
						<MetadataSummaryLine>
							<SummaryLabel>Year:</SummaryLabel>
							<SummaryValue>
								<DateRangeWrapper>
									<MonthPicker
										srcRange={dateRange.srcRange}
										stateRange={dateRange.stateRange}
										onConfirm={handleSpendYearChange}
									/>
								</DateRangeWrapper>
							</SummaryValue>
						</MetadataSummaryLine>
					</Modal>
				)}
				{exportStatus.showProgressBar && (
					<DownloadProgressbar
						error={exportStatus.fileDownloadFailed}
						successMessage="Generating benchmark file. You will receive a notification when the file has been generated"
						onClose={handleCloseProgressBar}
					/>
				)}
			</Main>
		</Wrapper>
	);
};

export default BenchmarkContainer;
